import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AppTopBar } from "@/components/AppTopBar";
import { BottomNav } from "@/components/BottomNav";

export function StudentShell({ title, showBack = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <AppTopBar title={title} showBack={showBack} />
      <div className="max-w-[520px] mx-auto px-4 pt-4 pb-[calc(96px+env(safe-area-inset-bottom))]">
        <Outlet />
      </div>
      <BottomNav />
    </div>
  );
}

/** Rotas permitidas para cada role de staff. Usado para confinar Operador
 * de Eventos APENAS ao leitor de QR + tela de eventos. */
const ROLE_ROUTE_WHITELIST = {
  operador_evento: ["/admin/leitor-qr", "/admin/eventos"],
  // comercial: pode ver leads + dashboards básicos
  comercial: ["/admin/leads"],
  // admin_master: tudo
};

export function AdminGuard({ children, allow = ["admin_master"] }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/admin/login" replace />;

  // Se o role do usuário não está na lista de allow desta rota específica,
  // mas tem alguma rota permitida → redireciona para a 1ª rota permitida.
  if (!allow.includes(user.role)) {
    const whitelist = ROLE_ROUTE_WHITELIST[user.role];
    if (whitelist && whitelist.length > 0) {
      // Se já está numa rota permitida (mas o allow desta página não inclui), apenas redireciona
      return <Navigate to={whitelist[0]} replace />;
    }
    return <Navigate to="/admin/login" replace />;
  }

  // Operador de Eventos não pode acessar qualquer rota fora do whitelist
  // (mesmo que o `allow` da rota inclua "operador_evento", o whitelist faz o confinamento)
  const whitelist = ROLE_ROUTE_WHITELIST[user.role];
  if (whitelist && !whitelist.some((path) => location.pathname.startsWith(path))) {
    return <Navigate to={whitelist[0]} replace />;
  }

  return children;
}

export function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) {
    // Roteia para a tela inicial CORRETA conforme o role:
    //   - operador_evento → direto pro leitor de QR (única tela que pode ver)
    //   - comercial → leads
    //   - admin_master → dashboard
    //   - aluno → home
    if (user.role === "operador_evento") return <Navigate to="/admin/leitor-qr" replace />;
    if (user.role === "comercial") return <Navigate to="/admin/leads" replace />;
    if (user.role === "admin_master") return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/home" replace />;
  }
  return children;
}
